import React, { useState, useEffect, ChangeEvent, useCallback } from "react";
import { useServices } from "../../../../hooks/useServices";
import { useCategories } from "../../../../hooks/useCategories";
import { useBranches } from "../../../../hooks/useBranches";
import { Category } from "../../../../types/api-responses";
import { ServiceRequest, UpdateServiceRequest } from "../../../../services/api";
import type { ServiceDetailsResponse, AgeGroup } from "../../../../types/api-responses";
import InputField from "../../../../components/InputField/InputField";
import Toggle from "../../../../components/Toggle/Toggle";
import Button from "../../../../components/Button/Button";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import ProgressBar from "../../../../components/ProgressBar/ProgressBar";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import SearchBar from "../../../../components/SearchBar/SearchBar";
import styles from './AddEditService.module.css';
import { useStaff } from "../../../../hooks/useStaff";
import { useUser } from "../../../../contexts/UserContext";
import { useLanguage } from "../../../../contexts/LanguageContext";
import type { BusinessStaffDto } from "../../../../types/api-responses";
import { normalizeServiceType } from "../../../../components/GenderGlyph";
import { normalizeAgeGroup } from "../../../../components/AgeGroupChip";
// PBI 1962 (AB#1968) — flexible option-groups builder (flag-gated, default OFF).
import ServiceOptionGroupsBuilder from "../../../../components/ServiceOptionGroupsBuilder/ServiceOptionGroupsBuilder";
import { isServiceOptionGroupsEnabled } from "../../../../lib/serviceOptionGroups/serviceOptionGroupsFlag";
import type { OptionGroupModel, OptionGroupWirePayload } from "../../../../types/serviceOptionGroups";
import {
    dtoToModel,
    durationStringToMinutes,
    makeDefaultVariantGroup,
    modelToWirePayload,
    modelToFlatPricingOptions,
    validateOptionGroups,
} from "../../../../lib/serviceOptionGroups/serviceOptionGroups";

/**
 * AB#1840 — BE now returns ServiceType as a string ("MenOnly" / "WomenOnly" /
 * "Everyone") per Faris F-Q2=A. This legacy wizard tracks it as a number.
 * Normalize on read so the chip-button comparisons (`=== 1`/`=== 2`/`=== 3`)
 * keep working without rewriting the wizard's internal state model.
 */
function serviceTypeAsNumber(raw: number | string | null | undefined): number {
    const s = normalizeServiceType(raw);
    if (s === 'MenOnly') return 1;
    if (s === 'WomenOnly') return 2;
    if (s === 'Everyone') return 3;
    return 1; // legacy default — wizard previously initialised to 1.
}

interface AddEditProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (createdService?: ServiceDetailsResponse) => void;
    editingServiceId?: number | null;
}

const AddEditService: React.FC<AddEditProps> = ({ onClose, onSuccess, isOpen, editingServiceId }) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [isLoadingService, setIsLoadingService] = useState(false);
    const { getBusinessCategories } = useCategories();
    const { createService, updateService, getService, loading } = useServices({ forOwner: true });
    const { getBranches } = useBranches();
    const [branches, setBranches] = useState<import("../../../../types/api-responses").BranchDto[]>([]);
    const [isLoadingBranches, setIsLoadingBranches] = useState(false);
    const { staff, getBusinessStaff, loading: staffLoading } = useStaff();
    const { user } = useUser();
    const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
    const [staffSearch, setStaffSearch] = useState<string>("");

    const isEditMode = !!editingServiceId;
    const { t } = useLanguage();

    // PBI 1962 (AB#1968) — option-groups builder (flag-gated, default OFF).
    const optionGroupsEnabled = isServiceOptionGroupsEnabled();
    const [optionGroups, setOptionGroups] = useState<OptionGroupModel[]>([]);
    const [optionGroupsTouched, setOptionGroupsTouched] = useState(false);

    // AB#1847 (PBI-I) — AgeGroup defaults to `null` so the create wizard
    // forces an explicit pick (REQUIRED per AC). Edit mode populates from
    // the BE row (back-fill = "Adult" for legacy data).
    const [serviceData, setServiceData] = useState<{
        categoryId: number;
        name: string;
        image: File | null;
        removeExistingImage: boolean;
        minDuration: string;
        maxDuration: string;
        serviceType: number;
        ageGroup: AgeGroup | null;
        branchId: number;
        hasHomeService: boolean;
        description: string;
        isActive: boolean;
        pricingOptions: Array<{
            name: string;
            price: number;
            currency: string;
            duration: string;
        }>;
    }>({
        categoryId: 0,
        name: '',
        image: null as File | null,
        removeExistingImage: false,
        minDuration: '00:30:00',
        maxDuration: '01:00:00',
        serviceType: 1,
        ageGroup: null,
        branchId: 0,
        hasHomeService: false,
        description: '',
        isActive: true,
        pricingOptions: [{ name: '', price: 0, currency: 'AED', duration: '00:30:00' }]
    });

    const fetchCategories = useCallback(async () => {
        setIsLoadingCategories(true);
        try {
            const response = await getBusinessCategories();
            setCategories(response.categories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setIsLoadingCategories(false);
        }
    }, [getBusinessCategories]);

    const fetchBranches = useCallback(async () => {
        setIsLoadingBranches(true);
        try {
            const response = await getBranches();
            // Only show active branches to users
            setBranches(response.branches.filter(b => b.active));
        } catch (error) {
            console.error('Error fetching branches:', error);
        } finally {
            setIsLoadingBranches(false);
        }
    }, [getBranches]);

    const loadServiceData = useCallback(async (serviceId: number) => {
        setIsLoadingService(true);
        try {
            const service = await getService(serviceId);
            setServiceData({
                categoryId: 0, // Will be set after categories are loaded
                name: service.name,
                image: null,
                removeExistingImage: false,
                minDuration: service.minDuration,
                maxDuration: service.maxDuration,
                serviceType: serviceTypeAsNumber(service.serviceType),
                // AB#1847 — edit mode: pre-fill from BE. Legacy rows
                // back-fill to "Adult" server-side, so this is non-null
                // in practice; if BE ever returns missing, default to
                // "Adult" silently rather than re-prompt the owner.
                ageGroup: normalizeAgeGroup(service.ageGroup) ?? 'Adult',
                branchId: service.branchId || 0,
                hasHomeService: service.hasHomeService,
                description: service.description || '',
                isActive: service.isActive,
                pricingOptions: service.pricingOptions.map(option => ({
                    name: option.name,
                    price: option.price,
                    currency: option.currency,
                    duration: option.duration
                }))
            });
            // Pre-select assigned staff if present
            if (service.assignedStaffIds && Array.isArray(service.assignedStaffIds)) {
                setSelectedStaffIds(service.assignedStaffIds);
            }
            // PBI 1962 — seed the option-groups builder from the DTO's groups
            // (present only when the backend flag is ON) or by wrapping the flat
            // pricing options into a default Variant group.
            if (optionGroupsEnabled) {
                const groups = service.optionGroups;
                if (groups && groups.length > 0) {
                    setOptionGroups(dtoToModel(groups));
                } else {
                    setOptionGroups([
                        makeDefaultVariantGroup(
                            service.pricingOptions.map(o => ({
                                name: o.name,
                                price: o.price,
                                durationMin: durationStringToMinutes(o.duration),
                            })),
                        ),
                    ]);
                }
            }
        } catch (error) {
            console.error('Error loading service:', error);
        } finally {
            setIsLoadingService(false);
        }
    }, [getService, optionGroupsEnabled]);

    useEffect(() => {
        if (isOpen) {
            fetchCategories();
            fetchBranches();
            if (user?.businessId) {
                getBusinessStaff({ businessId: user.businessId });
            }
            if (isEditMode && editingServiceId) {
                loadServiceData(editingServiceId);
            } else if (optionGroupsEnabled) {
                // PBI 1962 — create mode: start with a single default Variant
                // "Options" group (matches the backend default-group shape).
                setOptionGroups([makeDefaultVariantGroup()]);
                setOptionGroupsTouched(false);
            }
        }
    }, [isOpen, isEditMode, editingServiceId, fetchCategories, fetchBranches, loadServiceData, getBusinessStaff, user?.businessId, optionGroupsEnabled]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    const handleSubmit = async () => {
        try {
            // AB#1847 — AgeGroup is required at submit. Defensive guard:
            // step-1 validation already gates Next, but if a future caller
            // skips steps we still hard-fail here rather than POSTing null.
            if (!serviceData.ageGroup) {
                setStepErrors({ ageGroup: t('services.ageGroup.required') });
                setStep(1);
                return;
            }
            // PBI 1962 — when the builder is ON, block submit on invalid groups
            // and derive OptionGroupsJson + the flat PricingOptionsJson fallback
            // (dual-write). When OFF, send the flat options untouched (dark).
            let optionGroupsPayload: OptionGroupWirePayload[] | undefined;
            let pricingOptionsToSend = serviceData.pricingOptions;
            if (optionGroupsEnabled) {
                const validation = validateOptionGroups(optionGroups);
                if (!validation.ok) {
                    setOptionGroupsTouched(true);
                    setStep(2);
                    return;
                }
                const currency = serviceData.pricingOptions[0]?.currency ?? 'AED';
                optionGroupsPayload = modelToWirePayload(optionGroups, currency);
                pricingOptionsToSend = modelToFlatPricingOptions(optionGroups, currency);
            }
            if (isEditMode && editingServiceId) {
                const updateData: UpdateServiceRequest = {
                    branchId: serviceData.branchId,
                    name: serviceData.name,
                    image: serviceData.image || undefined,
                    removeExistingImage: serviceData.removeExistingImage,
                    minDuration: serviceData.minDuration,
                    maxDuration: serviceData.maxDuration,
                    serviceType: serviceData.serviceType,
                    ageGroup: serviceData.ageGroup,
                    hasHomeService: serviceData.hasHomeService,
                    description: serviceData.description,
                    isActive: serviceData.isActive,
                    pricingOptions: pricingOptionsToSend,
                    optionGroups: optionGroupsPayload,
                    staffIds: selectedStaffIds
                };
                await updateService(editingServiceId, updateData);
            } else {
                const requestData: ServiceRequest = {
                    categoryId: serviceData.categoryId,
                    branchId: serviceData.branchId,
                    name: serviceData.name,
                    image: serviceData.image || undefined,
                    minDuration: serviceData.minDuration,
                    maxDuration: serviceData.maxDuration,
                    serviceType: serviceData.serviceType,
                    ageGroup: serviceData.ageGroup,
                    hasHomeService: serviceData.hasHomeService,
                    description: serviceData.description,
                    pricingOptions: pricingOptionsToSend,
                    optionGroups: optionGroupsPayload,
                    staffIds: selectedStaffIds
                };
                const created = await createService(requestData);
                // #51: Build an optimistic service record from the request +
                // server response so the new service appears on the Services
                // list immediately, without waiting for the list refetch.
                const categoryName = categories.find(c => c.id === requestData.categoryId)?.name ?? '';
                const optimistic: ServiceDetailsResponse = {
                    id: created.serviceId,
                    branchId: requestData.branchId,
                    name: requestData.name,
                    imageUrl: '',
                    minDuration: requestData.minDuration,
                    maxDuration: requestData.maxDuration,
                    serviceType: requestData.serviceType,
                    ageGroup: requestData.ageGroup,
                    hasHomeService: requestData.hasHomeService,
                    description: requestData.description ?? '',
                    isActive: true,
                    categoryName,
                    pricingOptions: created.pricingOptions.map(p => ({
                        id: 0,
                        name: p.name,
                        price: p.price,
                        currency: p.currency,
                        duration: p.duration,
                    })),
                    assignedStaffIds: selectedStaffIds,
                };
                await onSuccess(optimistic);
                onClose();
                return;
            }
            await onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving service:', error);
        }
    };

    const [step, setStep] = useState(1);
    const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

    const nextStep = () => {
        if (step === 1) {
            // #1223: Show inline errors instead of silently blocking
            const errors: Record<string, string> = {};
            if (!serviceData.name.trim()) errors.name = 'Service name is required';
            if (!serviceData.categoryId) errors.categoryId = 'Please select a category';
            if (!serviceData.branchId) errors.branchId = 'Please select a branch';
            // AB#1847 (PBI-I) — AgeGroup is REQUIRED. The owner must
            // explicitly pick Adult / Kid / Both. We do NOT default to
            // Adult so customers always see an intentional choice.
            if (!serviceData.ageGroup) errors.ageGroup = t('services.ageGroup.required');
            if (Object.keys(errors).length > 0) { setStepErrors(errors); return; }
            setStepErrors({});
        }
        if (step === 2) {
            if (optionGroupsEnabled) {
                // PBI 1962 — gate on the mirrored backend rules instead of the
                // flat "some option has name+price" heuristic.
                const validation = validateOptionGroups(optionGroups);
                if (!validation.ok) { setOptionGroupsTouched(true); return; }
            } else {
                const hasValidPricing = serviceData.pricingOptions.some(
                    opt => opt.name.trim() && opt.price > 0
                );
                if (!hasValidPricing) { setStepErrors({ pricing: 'At least one pricing option is required' }); return; }
            }
            setStepErrors({});
        }
        if (step < 3) setStep(step + 1);
    };

    const prevStep = () => {
        if (step > 1) setStep(step - 1);
    };

    const handleInputChange = (name: string, value: string | number | boolean | File | null) => {
        setServiceData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleInputChange('image', file);
            handleInputChange('removeExistingImage', false);
        }
    };

    const handleRemoveImage = () => {
        handleInputChange('image', null);
        handleInputChange('removeExistingImage', true);
    };

    const handlePricingOptionAdd = () => {
        const newPricingOption = {
            name: "",
            price: 0,
            currency: "AED",
            duration: "00:30:00"
        };
        setServiceData(prev => ({
            ...prev,
            pricingOptions: [...prev.pricingOptions, newPricingOption]
        }));
    };

    const handlePricingOptionChange = (index: number, field: string, value: string | number) => {
        setServiceData(prev => {
            const updatedOptions = [...prev.pricingOptions];
            updatedOptions[index] = {
                ...updatedOptions[index],
                [field]: value
            };
            return {
                ...prev,
                pricingOptions: updatedOptions
            };
        });
    };

    const handlePricingOptionDelete = (index: number) => {
        setServiceData(prev => ({
            ...prev,
            pricingOptions: prev.pricingOptions.filter((_, i) => i !== index)
        }));
    };

    const getCategoryOptions = () => {
        return categories.map(cat => cat.name);
    };

    const getCategoryIdByName = (name: string) => {
        const category = categories.find(cat => cat.name === name);
        return category ? category.id : 0;
    };

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                {(loading || isLoadingService) && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}>
                            <svg viewBox="0 0 50 50">
                                <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                            </svg>
                            <span>{isEditMode ? 'Updating service...' : 'Creating service...'}</span>
                        </div>
                    </div>
                )}
                <div className={styles.progressWrappar}>
                    <button onClick={onClose} className={styles.closeButton} >&times;</button>
                    <ProgressBar progress={(step / 3) * 100} label={`Step ${step} out of 3`} />
                    <h2 className={`${styles.header} headerText`}>
                        {isEditMode ? 'Edit service' : 'Add service'}
                        {step === 1 && " - Service information"}
                        {step === 2 && " - Pricing & duration"}
                        {step === 3 && " - Assign employee"}
                    </h2>
                </div>
                <div className={styles.headerCloseInputsContainer}>
                    {step === 1 && (
                        <>
                            <div className={styles.uploadphoto}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    id="serviceImage"
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="serviceImage" className={styles.uploadphotolbl}>
                                    {serviceData.image ? (
                                        <img
                                            src={URL.createObjectURL(serviceData.image)}
                                            alt="service preview"
                                            className={styles.previewImage}
                                        />
                                    ) : (
                                        <>
                                            <img src="./assets/icons/uploadphoto.png" alt="upload" />
                                            <span className={styles.desc}>Upload service photo</span>
                                        </>
                                    )}
                                </label>
                                {isEditMode && (serviceData.image || !serviceData.removeExistingImage) && (
                                    <Button
                                        label="Remove Image"
                                        onClick={handleRemoveImage}
                                        size="small"
                                        variant="secondary"
                                    />
                                )}
                            </div>
                            <InputField
                                label="Service name"
                                name="name"
                                value={serviceData.name}
                                onChange={(value) => { handleInputChange('name', value); setStepErrors(prev => { const { name: _, ...rest } = prev; return rest; }); }}
                                placeholder="Service name"
                                feedback={stepErrors.name ? 'error' : undefined}
                                feedbackMessage={stepErrors.name}
                            />
                            {!isEditMode && (
                                <Dropdown
                                    label="Category"
                                    options={getCategoryOptions()}
                                    value={categories.find(cat => cat.id === serviceData.categoryId)?.name || ''}
                                    onChange={(value) => handleInputChange('categoryId', getCategoryIdByName(value as string))}
                                    isLoading={isLoadingCategories}
                                />
                            )}
                            <Dropdown
                                label="Branch"
                                options={branches.map(b => b.name)}
                                value={branches.find(b => b.id === serviceData.branchId)?.name || ''}
                                onChange={(value) => {
                                    const selected = branches.find(b => b.name === value);
                                    handleInputChange('branchId', selected ? selected.id : 0);
                                }}
                                isLoading={isLoadingBranches}
                                defaultMessage={isLoadingBranches ? 'Loading branches...' : 'Select a branch'}
                            />
                            <div className={styles.checkboxGroup}>
                                <Checkbox
                                    label="Men only"
                                    checked={serviceData.serviceType === 1}
                                    onChange={() => handleInputChange('serviceType', 1)}
                                    variant="button"
                                />
                                <Checkbox
                                    label="Women only"
                                    checked={serviceData.serviceType === 2}
                                    onChange={() => handleInputChange('serviceType', 2)}
                                    variant="button"
                                />
                                <Checkbox
                                    label="Everyone"
                                    checked={serviceData.serviceType === 3}
                                    onChange={() => handleInputChange('serviceType', 3)}
                                    variant="button"
                                />
                            </div>
                            {/* AB#1847 (PBI-I) / AB#1838 — Age-group axis of
                                the gender matrix. REQUIRED on Add/Edit; the
                                owner must explicitly pick Adult / Kid / Both
                                (no implicit default). AR `Both` = `كلاهما`
                                per founder Q1 (NOT `الجميع`, which is the
                                gender-axis `Everyone` label). */}
                            <div className={styles.checkboxGroupLabel}>
                                <span>{t('services.ageGroup.label')}</span>
                                {stepErrors.ageGroup && (
                                    <span className={styles.fieldError} role="alert">
                                        {stepErrors.ageGroup}
                                    </span>
                                )}
                            </div>
                            <div
                                className={styles.checkboxGroup}
                                role="radiogroup"
                                aria-label={t('services.ageGroup.label')}
                                aria-required="true"
                            >
                                <Checkbox
                                    label={t('services.ageGroup.adults')}
                                    checked={serviceData.ageGroup === 'Adult'}
                                    onChange={() => {
                                        handleInputChange('ageGroup', 'Adult');
                                        setStepErrors(prev => { const { ageGroup: _, ...rest } = prev; return rest; });
                                    }}
                                    variant="button"
                                />
                                <Checkbox
                                    label={t('services.ageGroup.kids')}
                                    checked={serviceData.ageGroup === 'Kid'}
                                    onChange={() => {
                                        handleInputChange('ageGroup', 'Kid');
                                        setStepErrors(prev => { const { ageGroup: _, ...rest } = prev; return rest; });
                                    }}
                                    variant="button"
                                />
                                <Checkbox
                                    label={t('services.ageGroup.both')}
                                    checked={serviceData.ageGroup === 'Both'}
                                    onChange={() => {
                                        handleInputChange('ageGroup', 'Both');
                                        setStepErrors(prev => { const { ageGroup: _, ...rest } = prev; return rest; });
                                    }}
                                    variant="button"
                                />
                            </div>
                            <Toggle
                                name='homeServicesAvailable'
                                label="Home service available"
                                checked={serviceData.hasHomeService}
                                onChange={(value) => handleInputChange('hasHomeService', value)}
                            />
                            {isEditMode && (
                                <Toggle
                                    name='isActive'
                                    label="Service active"
                                    checked={serviceData.isActive}
                                    onChange={(value) => handleInputChange('isActive', value)}
                                />
                            )}
                        </>
                    )}
                    {step === 2 && optionGroupsEnabled && (
                        <div className={styles.optionGroupsPanel}>
                            <ServiceOptionGroupsBuilder
                                value={optionGroups}
                                onChange={(next) => { setOptionGroups(next); setOptionGroupsTouched(true); }}
                                currency={serviceData.pricingOptions[0]?.currency ?? 'AED'}
                                disabled={loading || isLoadingService}
                                errorKeys={optionGroupsTouched ? validateOptionGroups(optionGroups).errorKeys : []}
                            />
                        </div>
                    )}
                    {step === 2 && !optionGroupsEnabled && (
                        <>
                            {serviceData.pricingOptions.map((option, index) => (
                                <div key={index} className={styles.pricingOptionContainer}>
                                    {serviceData.pricingOptions.length > 1 && (
                                        <div className={styles.deletebutton}>
                                            <Button
                                                label="Delete"
                                                onClick={() => handlePricingOptionDelete(index)}
                                                noAppearance={true}
                                                backgroundColor="transparent"
                                                size="small"
                                                fontColor="var(--destructive-fg)"
                                                icon={
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M17.5 4.98332C14.725 4.70832 11.9333 4.56665 9.15 4.56665C7.5 4.56665 5.85 4.64998 4.2 4.81665L2.5 4.98332" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M7.08331 4.14167L7.26665 3.05001C7.39998 2.25834 7.49998 1.66667 8.90831 1.66667H11.0916C12.5 1.66667 12.6083 2.29167 12.7333 3.05834L12.9166 4.14167" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path style={{ stroke: 'var(--destructive-fg)' }} d="M15.7084 7.61667L15.1667 16.0083C15.075 17.3167 15 18.3333 12.675 18.3333H7.32502C5.00002 18.3333 4.92502 17.3167 4.83335 16.0083L4.29169 7.61667" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                }
                                            />
                                        </div>
                                    )}
                                    <div className={styles.pricingOptionHeader}>
                                        <InputField
                                            label="Pricing option name"
                                            name={`pricingOption-${index}-name`}
                                            value={option.name}
                                            onChange={(value) => handlePricingOptionChange(index, 'name', value)}
                                            placeholder="Pricing option name"
                                        />
                                    </div>
                                    <InputField
                                        label="Duration"
                                        name={`pricingOption-${index}-duration`}
                                        value={option.duration}
                                        onChange={(value) => handlePricingOptionChange(index, 'duration', value)}
                                        placeholder="HH:mm:ss"
                                    />
                                    <InputField
                                        label="Price"
                                        name={`pricingOption-${index}-price`}
                                        value={option.price.toString()}
                                        onChange={(value) => handlePricingOptionChange(index, 'price', parseFloat(value))}
                                        type="number"
                                        unit="AED"
                                    />
                                </div>
                            ))}
                            <Button
                                label="Add pricing option"
                                onClick={handlePricingOptionAdd}
                                noAppearance={true}
                                backgroundColor="transparent"
                                iconPosition="left"
                                size="medium"
                                fontColor="var(--primary)"
                                icon={
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path style={{ stroke: 'var(--primary)' }} d="M10.0753 19L9.92529 1" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                        <path style={{ stroke: 'var(--primary)' }} d="M1 10.075L19 9.92505" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            />
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <div className={styles.searchSection}>
                                <SearchBar placeholder="Search employee" onSearch={setStaffSearch} />
                            </div>
                            {(staffLoading) && (
                                <div className={styles.loadingOverlay}>
                                    <div className={styles.spinner}>
                                        <svg viewBox="0 0 50 50">
                                            <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                                        </svg>
                                        <span>Loading staff...</span>
                                    </div>
                                </div>
                            )}
                            <div className={styles.empsContainer}>
                                {staff
                                    .filter((s: BusinessStaffDto) => (s.fullName || '').toLowerCase().includes(staffSearch.toLowerCase()))
                                    .map((s: BusinessStaffDto) => (
                                        <div key={s.id} className={styles.empCard}>
                                            <Checkbox
                                                checked={selectedStaffIds.includes(s.id)}
                                                onChange={() =>
                                                    setSelectedStaffIds(prev => prev.includes(s.id)
                                                        ? prev.filter(id => id !== s.id)
                                                        : [...prev, s.id])
                                                }
                                            />
                                            <img className={styles.empImg} src="./assets/icons/emp.png" alt="employee" />
                                            <div className={styles.empDetails}>
                                                <span className={styles.empName}>{s.fullName}</span>
                                                <span className={styles.empTitle}>{s.position || (s.role === 1 ? 'Staff Manager' : 'Staff')}</span>
                                            </div>
                                        </div>
                                    ))}
                                {(!staffLoading && staff.length === 0) && (
                                    <div style={{ padding: 12, color: 'var(--text-secondary)' }}>No staff found</div>
                                )}
                            </div>
                        </>
                    )}

                    <div className={styles.buttonContainer}>
                        <Button onClick={prevStep} label='Back' size='medium' noAppearance={true} disabled={step === 1} variant={step === 1 ? 'disabled' : 'primary'} />
                        <Button
                            onClick={step === 3 ? handleSubmit : nextStep}
                            label={step === 3 ? (isEditMode ? 'Update' : 'Done') : 'Next'}
                            size='medium'
                            disabled={loading || isLoadingService}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditService;